import { Graphics } from 'pixi.js';
import { type System, ComponentStore, type World } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type {
	AmmoComponent,
	EnemyComponent,
	PlayerState,
	TimeScaleResource,
	TransformComponent
} from '@game/scenes/characterPrototype/types';

export type ApplyTimeBoostFn = (world: World, value: number, duration: number) => void;

export type MeleeSystemOptions = {
	applyTimeBoost: ApplyTimeBoostFn;
};

export function createMeleeSystem(
	enemies: ComponentStore<EnemyComponent>,
	transforms: ComponentStore<TransformComponent>,
	graphics: ComponentStore<Graphics>,
	ammoGraphics: ComponentStore<Graphics>,
	ammos: ComponentStore<AmmoComponent>,
	states: ComponentStore<PlayerState>,
	options: MeleeSystemOptions
): System {
	return ({ delta, world }) => {
		const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
		const timescale = world.getResource<TimeScaleResource>('timescale') ?? ({ value: 1 } as TimeScaleResource);
		const dt = delta * timescale.value;
		const playerId = world.getResource<number>('player-entity');
		if (!playerId) return;
		const playerTransform = transforms.get(playerId);
		const playerState = states.get(playerId);
		if (!playerTransform || !playerState) return;

		if (playerState.meleeCooldown > 0) {
			playerState.meleeCooldown = Math.max(0, playerState.meleeCooldown - dt);
			return;
		}

		const r2 = tuning.meleeRadius * tuning.meleeRadius;
		const toKill: number[] = [];
		enemies.forEach((_enemy, enemyEntity) => {
			const enemyTransform = transforms.get(enemyEntity);
			if (!enemyTransform) return;
			const dx = enemyTransform.x - playerTransform.x;
			const dy = enemyTransform.y - playerTransform.y;
			if (dx * dx + dy * dy <= r2) {
				toKill.push(enemyEntity);
			}
		});

		if (!toKill.length) return;
		for (const enemyEntity of toKill) {
			const g = graphics.get(enemyEntity);
			if (g) {
				g.parent?.removeChild(g);
				g.destroy();
			}
			const ag = ammoGraphics.get(enemyEntity);
			if (ag) {
				ag.parent?.removeChild(ag);
				ag.destroy();
			}
			ammos.delete(enemyEntity);
			ammoGraphics.delete(enemyEntity);
			world.destroyEntity(enemyEntity);
		}
		options.applyTimeBoost(world, tuning.timeBoostMeleeValue, tuning.timeBoostDuration);
		playerState.meleeCooldown = tuning.meleeCooldown;
	};
}


